import React, { useEffect, useState } from "react";

export default function VideoModal({ title, src, webm, captions, lang, labels, testId = "video-modal", onClose, children }) {
  const ref = React.useRef(null);
  const [muted, setMuted] = useState(true);
  const [cc, setCc] = useState(() => {
    if (!captions) return lang;
    if (captions[lang]) return lang;
    if (captions.ro) return "ro";
    if (captions.en) return "en";
    return lang;
  });
  const [portrait, setPortrait] = useState(false);

  useEffect(() => {
    document.body.style.overflow = "hidden";
    const onKey = (e) => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", onKey);
    const v = ref.current;
    if (v) {
      v.muted = true;
      v.play().catch(() => {});
    }
    return () => { document.body.style.overflow = ""; window.removeEventListener("keydown", onKey); };
  }, [onClose]);

  useEffect(() => {
    const v = ref.current;
    if (!v) return;
    for (const tr of Array.from(v.textTracks)) tr.mode = tr.language === cc ? "showing" : "hidden";
  }, [cc]);

  const unmute = () => { const v = ref.current; if (v) { v.muted = false; setMuted(false); v.play().catch(() => {}); } };
  const availLangs = captions ? ["ro", "en"].filter((l) => captions[l]) : [];
  const ccOptions = [...availLangs.map((l) => [l, l.toUpperCase()]), ["off", labels.ccOff]];

  return (
    <div
      className="fixed inset-0 z-[70] flex items-center justify-center p-4"
      style={{ background: "rgba(10,10,8,0.85)", backdropFilter: "blur(6px)" }}
      onClick={onClose}
      role="dialog"
      aria-modal="true"
      data-testid={testId}
    >
      <div className="relative w-full" style={{ maxWidth: portrait ? 440 : 900 }} onClick={(e) => e.stopPropagation()}>
        <button onClick={onClose} aria-label="Close video" data-testid={`${testId}-close`} className="absolute -top-12 right-0 text-sm flex items-center gap-2" style={{ color: "#fbf8f1" }}>
          <span className="hidden sm:inline text-xs uppercase tracking-widest">{labels.close}</span>
          <span className="w-9 h-9 rounded-full flex items-center justify-center" style={{ background: "rgba(251,248,241,0.12)" }}>✕</span>
        </button>
        <div className="flex flex-wrap items-center justify-between gap-2 mb-3">
          <div className="text-xs uppercase tracking-widest" style={{ color: "#e2dcc6" }}>{title}</div>
          {captions && (
            <div className="flex items-center gap-1" data-testid={`${testId}-cc`}>
              <span className="text-[10px] uppercase tracking-widest mr-1" style={{ color: "#9b9582" }}>{labels.ccLabel}</span>
              {ccOptions.map(([k, label]) => (
                <button
                  key={k}
                  onClick={() => setCc(k)}
                  data-testid={`${testId}-cc-${k}`}
                  className="text-[11px] font-medium"
                  style={{ padding: "4px 10px", borderRadius: 999, background: cc === k ? "#fbf8f1" : "rgba(251,248,241,0.12)", color: cc === k ? "#0e0e0e" : "#fbf8f1", transition: "background-color .2s, color .2s" }}
                >
                  {label}
                </button>
              ))}
            </div>
          )}
        </div>
        <div className="relative rounded-2xl overflow-hidden shadow-2xl" style={{ background: "#000" }}>
          <video
            ref={ref}
            controls
            playsInline
            crossOrigin="anonymous"
            className="w-full"
            style={{ maxHeight: "72vh", background: "#000" }}
            onLoadedMetadata={(e) => setPortrait(e.currentTarget.videoHeight > e.currentTarget.videoWidth)}
            onVolumeChange={(e) => setMuted(e.currentTarget.muted)}
            data-testid={`${testId}-video`}
          >
            <source src={src} type="video/mp4" />
            {webm && <source src={webm} type="video/webm" />}
            {captions && captions.ro && <track kind="subtitles" srcLang="ro" label="Română" src={captions.ro} default={cc === "ro"} />}
            {captions && captions.en && <track kind="subtitles" srcLang="en" label="English" src={captions.en} default={cc === "en"} />}
          </video>
          {muted && (
            <button onClick={unmute} className="absolute top-4 left-4 btn-pill text-xs flex items-center gap-2" style={{ padding: "9px 16px", background: "#c9a961", color: "#0e0e0e", fontWeight: 600, boxShadow: "0 12px 30px -12px rgba(201,169,97,0.9)" }} data-testid={`${testId}-unmute`}>
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M11 5L6 9H2v6h4l5 4V5z" /><line x1="23" y1="9" x2="17" y2="15" /><line x1="17" y1="9" x2="23" y2="15" /></svg>
              {labels.tapSound}
            </button>
          )}
        </div>
        {children}
      </div>
    </div>
  );
}
